import React, { useMemo, useRef } from 'react';

import { Button, Modal, ListGroup, Row, Col, Badge } from '../../ui-kit';
import { IconUndo, IconSquareCheck, IconSquare, IconBars, IconCheck } from '../../ui-kit/icons';

const FilterModal = ({
  show,
  setShow,
  selectedColumns,
  columnsByKey,
  defaultColumnKeys,
  fetchData,
  setSelectedColumns
}) => {
  const draggedItem = useRef(null);
  const overItem = useRef(null);
  const allColumnKeys = useMemo(() => Object.keys(columnsByKey), [columnsByKey]);

  const selectedAvailableColumns = useMemo(
    () => selectedColumns.filter(key => allColumnKeys.includes(key)),
    [selectedColumns, allColumnKeys]
  );

  const handleApply = () => {
    setShow(false);
    fetchData();
  };

  const handleReset = () => setSelectedColumns(defaultColumnKeys);

  const selectAllColumns = () => setSelectedColumns(Object.keys(columnsByKey));

  const deselectAllColumns = () => setSelectedColumns([]);

  const selectColumn = key => {
    let newValue = null;
    newValue = selectedAvailableColumns.includes(key)
      ? selectedAvailableColumns.filter(k => k !== key)
      : [...selectedAvailableColumns, key];
    setSelectedColumns(newValue);
  };

  // This method is called at the beginning of the drag operation. It keeps
  // track of the list item being dragged.
  const dragStart = (e, index) => {
    draggedItem.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };

  // This method keeps track of the target list item that the dragged item is
  // hovering over. It will help us know where the item is going to be placed
  const dragOver = (e, index) => {
    e.preventDefault();
    // if draggedItem.current is zero, it would result in a falsy value so we
    // need to specifically check for null or undefined
    if (draggedItem.current === null || draggedItem.current === undefined) return;
    overItem.current = index;
  };

  // When the dragged item is dropped, it will update selected columns with the new order of columns.
  const dragEnd = () => {
    if (draggedItem.current === null || overItem.current === null) return;

    const updatedColumns = [...selectedAvailableColumns];
    const from = draggedItem.current;
    const to = overItem.current;

    const item = updatedColumns.splice(from, 1)[0];
    updatedColumns.splice(to, 0, item);

    setSelectedColumns(updatedColumns);
    draggedItem.current = null;
    overItem.current = null;
  };

  return (
    <Modal size="lg" show={show} onHide={() => setShow(false)}>
      <Modal.Header closeButton>
        <Modal.Title>{I18n.t('reports.select_columns')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Button variant="secondary" onClick={handleReset}>
          <IconUndo /> {I18n.t('reports.select_default_columns')}
        </Button>
        <Button className="me-1 ms-1" variant="secondary" onClick={selectAllColumns}>
          <IconSquareCheck /> {I18n.t('select_all')}
        </Button>
        <Button onClick={deselectAllColumns} variant="secondary">
          <IconSquare /> {I18n.t('deselect_all')}
        </Button>
        <Row className="mt-3">
          <Col>
            <h5>{I18n.t('reports.select_columns')}</h5>
            <ListGroup>
              {allColumnKeys.map(key => (
                <ListGroup.Item key={key} action onClick={() => selectColumn(key)}>
                  <Row>
                    <Col md={1}>{selectedAvailableColumns.includes(key) && <IconCheck />}</Col>
                    <Col md={11}>
                      <Badge>{columnsByKey[key]?.tag}</Badge> {columnsByKey[key]?.i18n_attribute}
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Col>
          <Col>
            <h5>{I18n.t('reports.order_columns')}</h5>
            <ListGroup>
              {selectedAvailableColumns.map((key, index) => (
                <ListGroup.Item
                  data-id={index}
                  key={key}
                  onDragStart={e => dragStart(e, index)}
                  onDragEnd={dragEnd}
                  onDragOver={e => dragOver(e, index)}
                  draggable
                >
                  <Row>
                    <Col md={1}>
                      <IconBars />
                    </Col>
                    <Col md={11}>{columnsByKey[key]?.i18n_attribute}</Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShow(false)}>
          {I18n.t('close')}
        </Button>
        <Button variant="success" onClick={handleApply}>
          {I18n.t('apply')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FilterModal;
